/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import { AppContext } from "@/App"
import { Checkbox } from "@/Components/ui/checkbox"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/Components/ui/form"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/Components/ui/select"
import { zodResolver } from "@hookform/resolvers/zod"
import { useContext, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { PlanBuilderContext, TaskGroupEdit } from "./Plan"
import { Input } from "../ui/input"

const formSchema = z.object({
    ownerId: z.string(),
    workstream: z.string(),
    workPackage: z.string(),
    isDeliverable: z.boolean(),
    isMilestone: z.boolean(),
    displayOnCharter: z.boolean(),
    displayOnStatusReport: z.boolean(),
    duration: z.preprocess(
        (val) => (val === "" ? undefined : Number(val)),
        z.number({
            invalid_type_error: "Duration must be a whole number",
        })
    ),
    setDependencies: z.enum(["cascade", "selected", "dependents", "remove"]),
    setSubtasks: z.boolean(),
})

interface GroupEditTasksProps {
    ids: number[];
    setDialogOpen?: (open: boolean) => void;
    groupEditTasks: (taskGroupEdit: TaskGroupEdit) => void;
    highlightedTask?: number;
}

const FrmGroupEditTasks = ({ ids, setDialogOpen, groupEditTasks, highlightedTask }: GroupEditTasksProps) => {
    const app = useContext(AppContext);
    const props = useContext(PlanBuilderContext);

    const [selectedTab, setSelectedTab] = useState("TaskProperties");
    const tabs = ["TaskProperties", "LinkedTaskOperations"];

    const [enableOwnerId, setEnableOwnerId] = useState(false);
    const [enableWorkPackage, setEnableWorkPackage] = useState(false);
    const [enableIsDeliverable, setEnableIsDeliverable] = useState(false);
    const [enableIsMilestone, setEnableIsMilestone] = useState(false);
    const [enableDisplayOnCharter, setEnableDisplayOnCharter] = useState(false);
    const [enableDisplayOnStatusReport, setEnableDisplayOnStatusReport] = useState(false);
    const [enableDuration, setEnableDuration] = useState(false);
    const [enableSetDependencies, setEnableSetDependencies] = useState(false);
    const [enableSetSubtasks, setEnableSetSubtasks] = useState(false);

    const [disallowSetSubtasks, setDisallowSetSubtasks] = useState(false);

    const [allowCascade, setAllowCascade] = useState(true);
    const [allowSelected, setAllowSelected] = useState(true);
    const [allowDependents, setAllowDependents] = useState(true);

    const enableSubmit = enableOwnerId || enableWorkPackage || enableIsDeliverable || enableIsMilestone || enableDisplayOnCharter || enableDisplayOnStatusReport || enableDuration || enableSetDependencies || enableSetSubtasks;

    useEffect(() => {
        getDependencyOptions();

        if(highlightedTask && props.getTaskById(highlightedTask)?.level === 2) {
            setEnableSetSubtasks(false);
            setDisallowSetSubtasks(true);
        } else {
            setDisallowSetSubtasks(false);
        }

    }, [ids, highlightedTask]);

    useEffect(() => {
        tabs.forEach((tab) => {
            const element = document.getElementById(tab);
            if (element) {
                if (tab === selectedTab) {
                    element.style.display = "block";
                } else {
                    element.style.display = "none";
                }
            }
        });
    }, [selectedTab]);

    const { reset, ...form } = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            ownerId: "",
            workstream: getWorkstream(),
            workPackage: "0",
            isDeliverable: false,
            isMilestone: false,
            displayOnCharter: false,
            displayOnStatusReport: false,
            duration: 1,
            setDependencies: allowCascade ? "cascade" : allowSelected ? "selected" : allowDependents ? "dependents" : "remove", // Default value for setDependencies
            setSubtasks: false,
        }
    });

    function getWorkstream() {
        if (!highlightedTask) return "0";
        const workstream = props.workstreams.find((ws) => ws.workPackages.some((wp) => wp.tasks.some((t) => t.id === highlightedTask)));
        return workstream?.id.toString() || "0";
    }

    function getDependencyOptions() {
        let previousTaskId = -1;
        let previousTaskDependents: number[] = [];

        ids.forEach((id) => {
            const dependents: number[] = [];
            props.getDependentTaskIds(id, dependents);

            if (dependents.includes(previousTaskId) || previousTaskDependents.includes(id)) {
                setAllowCascade(false);
            }

            if (highlightedTask && dependents.includes(highlightedTask)) {
                setAllowDependents(false);
            }

            if (highlightedTask && id === highlightedTask
                && dependents.some(depId => ids.includes(depId) && depId !== id)) {
                setAllowSelected(false);
            }

            previousTaskId = id;
            previousTaskDependents = [...dependents];
        });
    }

    function copyHighlightedTask() {
        if (highlightedTask) {
            const workstream = props.workstreams.find((ws) => ws.workPackages.some((wp) => wp.tasks.some((t) => t.id === highlightedTask)));
            const workPackage = workstream?.workPackages.find((wp) => wp.tasks.some((t) => t.id === highlightedTask));
            const task = workPackage?.tasks.find((t) => t.id === highlightedTask);

            if (!task) return;

            setEnableOwnerId(true);
            setEnableWorkPackage(true);
            setEnableIsDeliverable(true);
            setEnableIsMilestone(true);
            setEnableDisplayOnCharter(true);
            setEnableDisplayOnStatusReport(true);
            setEnableDuration(true);

            reset({
                ownerId: task.ownerId,
                workstream: workstream?.id.toString() || "0",
                workPackage: "0", // Reset workPackage initially
                isDeliverable: task.isDeliverable,
                isMilestone: task.isMilestone,
                displayOnCharter: task.displayOnCharter,
                displayOnStatusReport: task.displayOnStatusReport,
                duration: task.duration || 1,
                setDependencies: allowCascade ? "cascade" : allowSelected ? "selected" : allowDependents ? "dependents" : "remove", // Default value for setDependencies
            });

            // Set workPackage after ensuring workstream is set
            if (workstream && workPackage) {
                form.setValue("workPackage", workPackage.id.toString());
            }
        }
    }

    function onSubmit(data: z.infer<typeof formSchema>) {
        // make TaskGroupEdit object from data
        const taskGroupEdit: TaskGroupEdit = {
            highlightedTaskId: props.selectedTaskID!, // can't be null, because this form is only shown when a task is selected
            taskIDs: ids,
            enableOwnerId: enableOwnerId,
            enableWorkPackage: enableWorkPackage,
            enableIsDeliverable: enableIsDeliverable,
            enableIsMilestone: enableIsMilestone,
            enableDisplayOnCharter: enableDisplayOnCharter,
            enableDisplayOnStatusReport: enableDisplayOnStatusReport,
            enableDuration: enableDuration,
            enableSetDependencies: enableSetDependencies,
            enableSetSubtasks: enableSetSubtasks,
            ownerId: data.ownerId,
            workstream: Number.parseInt(data.workstream),
            workPackage: Number.parseInt(data.workPackage),
            isDeliverable: data.isDeliverable,
            isMilestone: data.isMilestone,
            displayOnCharter: data.displayOnCharter,
            displayOnStatusReport: data.displayOnStatusReport,
            duration: data.duration,
            setDependencies: data.setDependencies,
            setSubtasks: data.setSubtasks,
        }
        groupEditTasks(taskGroupEdit);

        setEnableOwnerId(false);
        setEnableWorkPackage(false);
        setEnableIsDeliverable(false);
        setEnableIsMilestone(false);
        setEnableDisplayOnCharter(false);
        setEnableDisplayOnStatusReport(false);
        setEnableDuration(false);
        setEnableSetDependencies(false);
        setEnableSetSubtasks(false);

        reset({
            ownerId: "",
            workstream: getWorkstream(),
            workPackage: "0",
            isDeliverable: false,
            isMilestone: false,
            displayOnCharter: false,
            displayOnStatusReport: false,
            duration: 1,
            setDependencies: allowCascade ? "cascade" : allowSelected ? "selected" : allowDependents ? "dependents" : "remove",
            setSubtasks: false,
        });
        setDialogOpen && setDialogOpen(false);
    }

    function onError(errors: any) {
        console.error(errors);
    }

    return (
        <Form {...form} reset={reset}>
            <form onSubmit={form.handleSubmit(onSubmit, onError)} className="flex flex-row w-full">
                <div className="flex flex-col space-y-4 w-full">
                    {highlightedTask && (
                        <div className="flex flex-col w-full">
                            <div className="flex flex-row w-full justify-between">
                                <h2 className="font-bold">Copy from Highlighted Task</h2>
                                <button type="button" className="button-default" onClick={() => copyHighlightedTask()}>Copy</button>
                            </div>
                        </div>)}
                    <div id="tabs" className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground w-min">
                        <button type="button" className={"inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium " + (selectedTab === "TaskProperties" ? "text-foreground bg-background" : "")} onClick={() => setSelectedTab("TaskProperties")} >Basic Properties</button>
                        <button type="button" className={"inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium " + (selectedTab === "LinkedTaskOperations" ? "text-foreground bg-background" : "")} onClick={() => setSelectedTab("LinkedTaskOperations")} >Linked Tasks</button>
                    </div>
                    <div id="TaskProperties" className="flex flex-col space-y-4 w-full">
                        <div className="flex flex-row w-full border-2 border-slate-200 p-4 rounded-md">
                            <div className="flex flew-col w-[10%] my-auto">
                                <Checkbox id="enableOwnerId" onCheckedChange={(checked) => setEnableOwnerId(checked as boolean)} checked={enableOwnerId} />
                            </div>
                            <div className="flex flex-col w-[90%]">
                                <FormField
                                    control={form.control}
                                    name="ownerId"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className={!enableOwnerId ? "text-muted-foreground" : ""}>Owner</FormLabel>
                                            <Select onValueChange={field.onChange} value={field.value} disabled={!enableOwnerId}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Assign to Team Member" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {app.teamMembers?.map((t) => (
                                                        <SelectItem key={t.id} value={t.id as string}>{t.email}</SelectItem>
                                                    ))}
                                                    {app.projectManagers?.map((m) => (
                                                        <SelectItem key={m.id} value={m.id as string}>{m.email}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        <div className="flex flex-row w-full border-2 border-slate-200 px-4 pt-2 pb-4 rounded-md">
                            <div className="flex flew-col w-[10%] mt-2">
                                <Checkbox id="enableIsDeliverable" onCheckedChange={(checked) => setEnableIsDeliverable(checked as boolean)} checked={enableIsDeliverable} />
                            </div>
                            <div className="flex flex-col w-[90%] px-2">
                                <div className="w-full relative">
                                    <FormField
                                        control={form.control}
                                        name="isDeliverable"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-end">
                                                <FormLabel className={!enableIsDeliverable ? "text-muted-foreground mx-2" : "mx-2"}>Deliverable</FormLabel>
                                                <FormControl>
                                                    <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={!enableIsDeliverable} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row w-full border-2 border-slate-200 px-4 pt-2 pb-4 rounded-md">
                            <div className="flex flew-col w-[10%] mt-2">
                                <Checkbox id="enableIsMilestone" onCheckedChange={(checked) => setEnableIsMilestone(checked as boolean)} checked={enableIsMilestone} />
                            </div>
                            <div className="flex flex-col w-[90%] px-2">
                                <div className="w-full relative">
                                    <FormField
                                        control={form.control}
                                        name="isMilestone"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-end">
                                                <FormLabel className={!enableIsMilestone ? "text-muted-foreground mx-2" : "mx-2"}>Milestone</FormLabel>
                                                <FormControl>
                                                    <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={!enableIsMilestone} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row w-full border-2 border-slate-200 px-4 pt-2 pb-4 rounded-md">
                            <div className="flex flew-col w-[10%] mt-2">
                                <Checkbox id="enableDisplayOnCharter" onCheckedChange={(checked) => { setEnableDisplayOnCharter(checked as boolean); }} checked={enableDisplayOnCharter} />
                            </div>
                            <div className="flex flex-col w-[90%] px-2">
                                <div className="w-full relative">
                                    <FormField
                                        control={form.control}
                                        name="displayOnCharter"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-end">
                                                <FormLabel className={!enableDisplayOnCharter ? "text-muted-foreground mx-2" : "mx-2"}>Display on Charter</FormLabel>
                                                <FormControl>
                                                    <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={!enableDisplayOnCharter} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row w-full border-2 border-slate-200 px-4 pt-2 pb-4 rounded-md">
                            <div className="flex flew-col w-[10%] mt-2">
                                <Checkbox id="enableDisplayOnStatusReport" onCheckedChange={(checked) => setEnableDisplayOnStatusReport(checked as boolean)} checked={enableDisplayOnStatusReport} />
                            </div>
                            <div className="flex flex-col w-[90%] px-2">
                                <div className="w-full relative">
                                    <FormField
                                        control={form.control}
                                        name="displayOnStatusReport"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-end">
                                                <FormLabel className={!enableDisplayOnStatusReport ? "text-muted-foreground mx-2" : "mx-2"}>Display on Status Report</FormLabel>
                                                <FormControl className="">
                                                    <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={!enableDisplayOnStatusReport} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div id="LinkedTaskOperations" className="flex flex-col space-y-4 w-full">
                        <div className="flex flex-row w-full border-2 border-slate-200 p-4 rounded-md">
                            <div className="flex flew-col w-[10%] my-auto">
                                <Checkbox id="enableCompleted" onCheckedChange={(checked) => { setEnableWorkPackage(checked as boolean); setEnableSetSubtasks(false) }} checked={enableWorkPackage} />
                            </div>
                            <div className={"flex flex-col w-[90%] my-auto "}>
                                <div className={(app.selectedProject?.projectTypeId === 1 ? "hidden" : "")}>
                                    <FormField
                                        control={form.control}
                                        name="workstream"
                                        defaultValue="0"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className={!enableWorkPackage ? "text-muted-foreground" : ""}>Workstream</FormLabel>
                                                <Select onValueChange={field.onChange} value={field.value.toString()} disabled={!enableWorkPackage}>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select a Workstream" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>{app.workPackageOptions?.map((ws) => (
                                                        <SelectItem key={ws.workstreamId} value={ws.workstreamId.toString()}>{ws.workstreamName}</SelectItem>
                                                    ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <FormField
                                    control={form.control}
                                    name="workPackage"
                                    defaultValue="0"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className={!enableWorkPackage ? "text-muted-foreground" : ""}>Work Package</FormLabel>
                                            <Select onValueChange={field.onChange} value={field.value.toString()} disabled={!enableWorkPackage}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select a work package" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {/* {app.workPackageOptions?.find((ws) => ws.workstreamId.toString() === form.watch("workstream"))?.workPackageNames?.map((wp) => (
                                                        <SelectItem key={wp.workPackageId} value={wp.workPackageId.toString()}>{wp.workPackageName}</SelectItem>
                                                    ))} */}
                                                    {props.workstreams[0].workPackages.map((wp) => (
                                                        <SelectItem key={wp.id} value={wp.id.toString()}>{wp.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        <div className="flex flex-row w-full border-2 border-slate-200 px-4 pt-2 pb-4 rounded-md">
                            <div className="flex flew-col w-[10%] mt-2">
                                <Checkbox id="enableDuration" onCheckedChange={(checked) => setEnableDuration(checked as boolean)} checked={enableDuration} />
                            </div>
                            <div className="">
                                <FormField
                                    control={form.control}
                                    name="duration"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Duration</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Duration" {...field} disabled={!enableDuration} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        <div className="flex flex-row w-full border-2 border-slate-200 px-4 pt-2 pb-4 rounded-md">
                            <div className="flex flew-col w-[10%] mt-2">
                                <Checkbox id="enableSetDependencies" onCheckedChange={(checked) => { setEnableSetDependencies(checked as boolean); setEnableSetSubtasks(false); }} checked={enableSetDependencies} />
                            </div>
                            <div className="">
                                <FormField
                                    control={form.control}
                                    name="setDependencies"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className={!enableOwnerId ? "text-muted-foreground" : ""}>Set Dependencies </FormLabel>
                                            <Select onValueChange={field.onChange} value={field.value} disabled={!enableSetDependencies}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select how to assign dependencies" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {allowSelected && <SelectItem key={"selected"} value={"selected"}>Selected are dependencies of highlighted Task</SelectItem>}
                                                    {allowDependents && <SelectItem key={"dependents"} value={"dependents"}>Selected depend on highlighted Task</SelectItem>}
                                                    {allowCascade && <SelectItem key={"cascade"} value={"cascade"}>Cascade dependencies</SelectItem>}
                                                    <SelectItem key={"remove"} value={"remove"}>Remove dependencies</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        <div className="flex flex-row w-full border-2 border-slate-200 px-4 pt-2 pb-4 rounded-md">
                            <div className="flex flex-col w-[10%] mt-2">
                                <Checkbox id="enableSetSubtasks" 
                                        onCheckedChange={(checked) => { setEnableSetSubtasks(checked as boolean); setEnableSetDependencies(false); setEnableWorkPackage(false); }} 
                                        checked={enableSetSubtasks} 
                                        disabled={disallowSetSubtasks}
                                        />
                            </div>
                            <div className="flex flex-col w-[90%] px-2">
                                <div className="w-full relative">
                                    Set selected as subtasks of highlighted
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="">
                        <button type="submit" disabled={!enableSubmit} className="button-default w-full mr-2">Update Tasks</button>
                    </div>
                </div>
            </form>
        </Form >
    )
}

export default FrmGroupEditTasks